import type { CommunityUser } from '@/mocks/adminCommunity';
import { tierLabels, tierIcons, tierColors } from '@/mocks/adminCommunity';

interface UserCardProps {
  user: CommunityUser;
  showRank?: boolean;
}

export default function UserCard({ user, showRank = false }: UserCardProps) {
  const colors = tierColors[user.tier];
  const rankMedal = user.topFanBadge === 'oro' ? '🥇' : user.topFanBadge === 'plata' ? '🥈' : user.topFanBadge === 'bronce' ? '🥉' : null;

  return (
    <div className={`flex-shrink-0 w-[260px] rounded-2xl border ${colors.border} ${colors.bg} p-4 flex flex-col gap-3 hover:scale-[1.02] transition-transform duration-200 cursor-default`}>
      {/* Header: avatar + tier badge */}
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-full bg-background-200 flex items-center justify-center overflow-hidden">
            <i className="ri-user-line text-foreground-400 text-xl"></i>
          </div>
          {user.isActive && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-background-50"></span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-foreground-950 font-bold text-sm truncate">{user.name}</p>
            {rankMedal && <span className="text-sm shrink-0">{rankMedal}</span>}
          </div>
          <p className="text-foreground-400 text-[11px]">{user.username}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-[10px]">{user.countryFlag}</span>
            <span className="text-foreground-500 text-[10px]">{user.country}</span>
          </div>
        </div>
      </div>

      {/* Tier badge */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${colors.badge} ${colors.badgeText} text-[10px] font-semibold`}>
          <i className={`${tierIcons[user.tier]} text-[10px]`}></i>
          {tierLabels[user.tier]}
        </span>

        {showRank && user.topFanRank && (
          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-600 text-[10px] font-semibold">
            #{user.topFanRank}
          </span>
        )}

        {user.tier === 'vip' && user.vipLevel && (
          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-violet-200 text-violet-800 text-[10px] font-semibold">
            Nivel {user.vipLevel}
          </span>
        )}

        {user.tier === 'embajador' && user.embajadorRegion && (
          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800 text-[10px] font-semibold">
            📍 {user.embajadorRegion}
          </span>
        )}
      </div>

      {/* Bio */}
      {user.bio && (
        <p className="text-foreground-500 text-[11px] leading-relaxed line-clamp-2">{user.bio}</p>
      )}

      {/* Stats row */}
      <div className="flex items-center gap-4 pt-1 border-t border-background-200/60">
        <div className="flex items-center gap-1">
          <i className="ri-flashlight-line text-foreground-400 text-xs"></i>
          <span className="text-foreground-700 text-xs font-semibold">{user.interactions}</span>
          <span className="text-foreground-400 text-[10px]">interacciones</span>
        </div>
        {user.tier === 'embajador' && user.embajadorLogros && (
          <div className="flex items-center gap-1">
            <i className="ri-trophy-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-700 text-xs font-semibold">{user.embajadorLogros.length}</span>
            <span className="text-foreground-400 text-[10px]">logros</span>
          </div>
        )}
      </div>

      {/* VIP / Embajador extra info */}
      {user.tier === 'vip' && user.vipSince && (
        <div className="flex items-center gap-1 text-foreground-400 text-[10px]">
          <i className="ri-calendar-line text-[10px]"></i>
          Miembro desde {new Date(user.vipSince).toLocaleDateString('es-CR', { year: 'numeric', month: 'short' })}
        </div>
      )}
      {user.tier === 'embajador' && user.embajadorLogros && user.embajadorLogros.length > 0 && (
        <div className="space-y-0.5">
          {user.embajadorLogros.slice(0, 2).map((logro, i) => (
            <div key={i} className="flex items-start gap-1 text-foreground-500 text-[10px]">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-[10px] mt-0.5 shrink-0"></i>
              <span className="leading-tight">{logro}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}